// EegClinicianDashboard.js
// Clinician view: MRI lesion input, protocol decisions, patient progress, alerts.
// Wires to api/eeg/* endpoints defined in redux/requests/apis.js

import React, { useEffect, useState, useCallback } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";

// Auth / API helpers
import { getToken, getUser } from "redux/services";
import {
  EEG_PATIENT_API,
  EEG_MRI_API,
  EEG_PROGRESS_API,
  EEG_PROGRESS_ERD_API,
  EEG_PROGRESS_ASYM_API,
  EEG_PROTOCOL_API,
  EEG_ALERTS_API,
  EEG_DAY1_API,
} from "redux/requests/apis";

/* ── helpers ───────────────────────────────────────────────────────────── */

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

async function apiFetch(url, opts = {}) {
  const res = await fetch(url, { headers: authHeaders(), ...opts });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

/* ── Panel header (same pattern as BrainActivation.js) ─────────────────── */
const PanelHeader = ({ icon, title, color = "dark" }) => (
  <MDBox display="flex">
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="4rem"
      height="4rem"
      variant="gradient"
      bgColor={color}
      color="white"
      shadow="md"
      borderRadius="xl"
      ml={3}
      mt={-2}
    >
      <Icon fontSize="medium" color="inherit">{icon}</Icon>
    </MDBox>
    <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
      {title}
    </MDTypography>
  </MDBox>
);

/* ── Stat chip ──────────────────────────────────────────────────────────── */
const StatBox = ({ label, value, unit = "", color = "info" }) => (
  <MDBox textAlign="center" px={1}>
    <MDTypography variant="h4" color={color} fontWeight="bold">
      {value ?? "—"}{unit}
    </MDTypography>
    <MDTypography variant="caption" color="text">{label}</MDTypography>
  </MDBox>
);

/* ── MRI form initial state ─────────────────────────────────────────────── */
const MRI_INIT = {
  lesionMniX: "", lesionMniY: "", lesionMniZ: "",
  lesionVolumeMl: "",
  hemisphereAffected: "left",
  m1Affected: false, pmcAffected: false, smaAffected: false, s1Affected: false,
  cstIntegrity: "",
  clinicalNotes: "",
};

/* ══════════════════════════════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════════════════════════════ */
export default function EegClinicianDashboard() {
  // The clinician selects a patient from the URL param or a picker
  // For now we read the logged-in user's linked patient id from the cookie
  // and fall back to the query string ?userId=...
  const params = new URLSearchParams(window.location.search);
  const userId = params.get("userId") || getUser();

  const [patient,  setPatient]  = useState(null);
  const [mri,      setMri]      = useState(null);
  const [progress, setProgress] = useState(null);
  const [erdData,  setErdData]  = useState([]);
  const [asymData, setAsymData] = useState([]);
  const [protocol, setProtocol] = useState(null);
  const [alerts,   setAlerts]   = useState(null);
  const [day1,     setDay1]     = useState(null);

  const [mriForm,    setMriForm]    = useState(MRI_INIT);
  const [mriSaving,  setMriSaving]  = useState(false);
  const [mriError,   setMriError]   = useState(null);
  const [mriSuccess, setMriSuccess] = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  /* ── load all panels in parallel ───────────────────────────────────────── */
  const load = useCallback(async () => {
    if (!userId) { setError("No patient selected."); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const [pat, mriRes, prog, erd, asym, prot, alrt, d1] = await Promise.allSettled([
        apiFetch(EEG_PATIENT_API(userId)),
        apiFetch(EEG_MRI_API(userId)),
        apiFetch(EEG_PROGRESS_API(userId)),
        apiFetch(EEG_PROGRESS_ERD_API(userId)),
        apiFetch(EEG_PROGRESS_ASYM_API(userId)),
        apiFetch(EEG_PROTOCOL_API(userId)),
        apiFetch(EEG_ALERTS_API(userId)),
        apiFetch(EEG_DAY1_API(userId)),
      ]);
      if (pat.status  === "fulfilled") setPatient(pat.value);
      if (mriRes.status === "fulfilled") { setMri(mriRes.value); populateMriForm(mriRes.value); }
      if (prog.status === "fulfilled") setProgress(prog.value);
      if (erd.status  === "fulfilled") setErdData(erd.value);
      if (asym.status === "fulfilled") setAsymData(asym.value);
      if (prot.status === "fulfilled") setProtocol(prot.value);
      if (alrt.status === "fulfilled") setAlerts(alrt.value);
      if (d1.status   === "fulfilled") setDay1(d1.value);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  function populateMriForm(m) {
    if (!m) return;
    setMriForm({
      lesionMniX: m.lesionMniX ?? "",
      lesionMniY: m.lesionMniY ?? "",
      lesionMniZ: m.lesionMniZ ?? "",
      lesionVolumeMl: m.lesionVolumeMl ?? "",
      hemisphereAffected: m.hemisphereAffected ?? "left",
      m1Affected: m.m1Affected ?? false,
      pmcAffected: m.pmcAffected ?? false,
      smaAffected: m.smaAffected ?? false,
      s1Affected: m.s1Affected ?? false,
      cstIntegrity: m.cstIntegrity ?? "",
      clinicalNotes: m.clinicalNotes ?? "",
    });
  }

  /* ── MRI save ───────────────────────────────────────────────────────────── */
  async function saveMri(e) {
    e.preventDefault();
    setMriSaving(true); setMriError(null); setMriSuccess(false);
    try {
      const method = mri ? "PUT" : "POST";
      const body   = {
        userId,
        lesionMniX:       parseFloat(mriForm.lesionMniX),
        lesionMniY:       parseFloat(mriForm.lesionMniY),
        lesionMniZ:       parseFloat(mriForm.lesionMniZ),
        lesionVolumeMl:   mriForm.lesionVolumeMl ? parseFloat(mriForm.lesionVolumeMl) : null,
        hemisphereAffected: mriForm.hemisphereAffected,
        m1Affected:   mriForm.m1Affected,
        pmcAffected:  mriForm.pmcAffected,
        smaAffected:  mriForm.smaAffected,
        s1Affected:   mriForm.s1Affected,
        cstIntegrity: mriForm.cstIntegrity ? parseFloat(mriForm.cstIntegrity) : null,
        clinicalNotes: mriForm.clinicalNotes,
      };
      const saved = await apiFetch(EEG_MRI_API(userId), {
        method, body: JSON.stringify(body),
      });
      setMri(saved);
      setMriSuccess(true);
      await load();
    } catch (err) {
      setMriError(err.message);
    } finally {
      setMriSaving(false);
    }
  }

  /* ── chart data ─────────────────────────────────────────────────────────── */
  const asymChart = {
    labels: asymData.map((d) => `S${d.sessionNumber}`),
    datasets: [
      {
        label: "Beta Asymmetry (C3/C4)",
        color: "info",
        data: asymData.map((d) => parseFloat((d.betaAsymmetry ?? 0).toFixed(3))),
      },
    ],
  };

  const erdChart = {
    labels: erdData.map((d) => `S${d.sessionNumber}`),
    datasets: [
      { label: "ERD Alpha %", color: "success", data: erdData.map((d) => d.erdAlpha ?? 0) },
      { label: "ERD Beta %",  color: "warning", data: erdData.map((d) => d.erdBeta  ?? 0) },
    ],
  };

  /* ── render ─────────────────────────────────────────────────────────────── */
  if (loading) return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox display="flex" justifyContent="center" mt={8}><CircularProgress /></MDBox>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ── Row 0: Patient summary strip ─────────────────────────────── */}
        {patient && (
          <Card sx={{ mb: 3, p: 2 }}>
            <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={3}>
              <MDBox>
                <MDTypography variant="h5" fontWeight="bold">
                  {patient.affectedSide?.toUpperCase()} STROKE — {patient.fmaSeverity?.toUpperCase()}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Day {patient.daysSinceStroke} post-stroke · Age {patient.age} · FMA {patient.fmaUpper}/66
                </MDTypography>
              </MDBox>
              <MDBox display="flex" gap={3} flexWrap="wrap" ml="auto">
                <StatBox label="FMA Upper" value={patient.fmaUpper} unit="/66" color="info" />
                <StatBox label="Grip ratio" value={patient.gripAsymmetry} color="success" />
                <StatBox label="MoCA"    value={patient.moca}    unit="/30" />
                <StatBox label="Barthel" value={patient.barthel} unit="/100" />
              </MDBox>
            </MDBox>
          </Card>
        )}

        {/* ── Row 1: MRI lesion input (CRITICAL — required before EEG) ──── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <PanelHeader icon="biotech" title="MRI Lesion Input" color="error" />
              {!mri && (
                <Alert severity="warning" sx={{ mx: 3, mb: 1 }}>
                  No MRI data entered. EEG sessions are blocked until this is saved.
                </Alert>
              )}
              {mriSuccess && <Alert severity="success" sx={{ mx: 3, mb: 1 }}>Saved successfully.</Alert>}
              {mriError   && <Alert severity="error"   sx={{ mx: 3, mb: 1 }}>{mriError}</Alert>}
              <MDBox px={3} pb={3} component="form" onSubmit={saveMri}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField label="MNI X" size="small" fullWidth required type="number"
                      value={mriForm.lesionMniX}
                      onChange={(e) => setMriForm(f => ({ ...f, lesionMniX: e.target.value }))} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField label="MNI Y" size="small" fullWidth required type="number"
                      value={mriForm.lesionMniY}
                      onChange={(e) => setMriForm(f => ({ ...f, lesionMniY: e.target.value }))} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField label="MNI Z" size="small" fullWidth required type="number"
                      value={mriForm.lesionMniZ}
                      onChange={(e) => setMriForm(f => ({ ...f, lesionMniZ: e.target.value }))} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="Volume (ml)" size="small" fullWidth type="number"
                      value={mriForm.lesionVolumeMl}
                      onChange={(e) => setMriForm(f => ({ ...f, lesionVolumeMl: e.target.value }))} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="CST Integrity (0–1)" size="small" fullWidth type="number"
                      inputProps={{ step: 0.01, min: 0, max: 1 }}
                      value={mriForm.cstIntegrity}
                      onChange={(e) => setMriForm(f => ({ ...f, cstIntegrity: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12}>
                    <MDTypography variant="caption" color="text">Hemisphere affected</MDTypography>
                    <MDBox display="flex" gap={1} mt={0.5}>
                      {["left", "right"].map((h) => (
                        <Chip key={h} label={h.toUpperCase()}
                          color={mriForm.hemisphereAffected === h ? "primary" : "default"}
                          onClick={() => setMriForm(f => ({ ...f, hemisphereAffected: h }))}
                          clickable />
                      ))}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12}>
                    <MDTypography variant="caption" color="text">Regions affected (from radiology report)</MDTypography>
                    <MDBox display="flex" flexWrap="wrap" gap={1} mt={0.5}>
                      {["m1Affected","pmcAffected","smaAffected","s1Affected"].map((key) => (
                        <FormControlLabel key={key}
                          control={
                            <Switch checked={mriForm[key]}
                              onChange={(e) => setMriForm(f => ({ ...f, [key]: e.target.checked }))} />
                          }
                          label={key.replace("Affected","").toUpperCase()}
                        />
                      ))}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Clinical Notes" size="small" fullWidth multiline rows={2}
                      value={mriForm.clinicalNotes}
                      onChange={(e) => setMriForm(f => ({ ...f, clinicalNotes: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12}>
                    <MDButton type="submit" variant="gradient" color="error" fullWidth disabled={mriSaving}>
                      {mriSaving ? <CircularProgress size={18} color="inherit" /> : (mri ? "Update MRI Data" : "Save MRI Data")}
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          {/* ── MRI computed outputs ─────────────────────────────────────── */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <PanelHeader icon="hub" title="Computed Electrode Targets" color="dark" />
              {mri ? (
                <MDBox px={3} pb={3}>
                  <MDBox display="flex" gap={2} mb={2} flexWrap="wrap">
                    <StatBox label="Dist to M1" value={mri.distanceToM1Mm} unit=" mm" color="info" />
                    <StatBox label="Dist to PMC" value={mri.distanceToPmcMm} unit=" mm" />
                  </MDBox>
                  <Divider />
                  <MDBox mt={2}>
                    <LabelValue label="Prognosis"   value={mri.prognosis} />
                    <LabelValue label="Strategy"    value={mri.recommendedStrategy} />
                    <LabelValue label="Primary electrode"   value={mri.primaryTargetElectrode}   color="success" bold />
                    <LabelValue label="Watch electrode"     value={mri.secondaryWatchElectrode}  color="info" />
                    <LabelValue label="Suppress electrode"  value={mri.suppressElectrode}        color="warning" />
                  </MDBox>
                </MDBox>
              ) : (
                <MDBox px={3} pb={3}>
                  <MDTypography variant="button" color="text" fontStyle="italic">
                    Enter MRI data on the left to compute electrode targets.
                  </MDTypography>
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>

        {/* ── Row 2: Protocol + Alerts ─────────────────────────────────── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <PanelHeader icon="electric_bolt" title="Adaptive Protocol" color="warning" />
              {protocol ? (
                <MDBox px={3} pb={3}>
                  <MDBox
                    p={2} mb={2} borderRadius="lg"
                    sx={{ background: "linear-gradient(195deg, #42424a, #191919)" }}
                  >
                    <MDTypography variant="h5" color="white" fontWeight="bold">
                      {protocol.protocolName?.replace(/_/g, " ").toUpperCase()}
                    </MDTypography>
                    <MDTypography variant="caption" color="white" opacity={0.8}>
                      {protocol.rationale}
                    </MDTypography>
                  </MDBox>
                  <LabelValue label="Primary electrode" value={protocol.primaryElectrode}  color="success" bold />
                  <LabelValue label="Watch electrode"   value={protocol.secondaryElectrode} color="info" />
                  <LabelValue label="Suppress"          value={protocol.suppressElectrode}  color="warning" />
                  <LabelValue label="Source"            value={protocol.source} />
                  <LabelValue label="Clinician approved" value={protocol.clinicianApproved ? "Yes" : "Pending"} />
                </MDBox>
              ) : (
                <MDBox px={3} pb={3}>
                  <MDTypography variant="button" color="text" fontStyle="italic">
                    Protocol will appear after MRI data is entered.
                  </MDTypography>
                </MDBox>
              )}
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <PanelHeader icon="warning_amber" title="Active Alerts" color="error" />
              <MDBox px={3} pb={3}>
                {alerts?.count === 0 && (
                  <Alert severity="success">No active alerts for latest session.</Alert>
                )}
                {(alerts?.alerts || []).map((a, i) => (
                  <Alert key={i} severity="warning" sx={{ mb: 1 }}>{a}</Alert>
                ))}
                {!alerts && (
                  <MDTypography variant="button" color="text" fontStyle="italic">
                    No session data yet.
                  </MDTypography>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* ── Row 3: Progress charts ───────────────────────────────────── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            {asymData.length > 0 ? (
              <DefaultLineChart
                icon={{ color: "info", component: "show_chart" }}
                title="Hemisphere Asymmetry Recovery"
                description="Beta C3/C4 asymmetry index — moves toward 0 as motor cortex rebalances"
                height="14rem"
                chart={asymChart}
              />
            ) : (
              <Card sx={{ height: "14rem" }}>
                <PanelHeader icon="show_chart" title="Hemisphere Asymmetry Recovery" color="info" />
                <MDBox px={3} pt={1}>
                  <MDTypography variant="button" color="text" fontStyle="italic">
                    No session data yet.
                  </MDTypography>
                </MDBox>
              </Card>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {erdData.length > 0 ? (
              <DefaultLineChart
                icon={{ color: "success", component: "trending_down" }}
                title="Motor Cortex ERD Trend"
                description="Alpha & Beta ERD % at target electrode — stronger dip = better motor cortex engagement"
                height="14rem"
                chart={erdChart}
              />
            ) : (
              <Card sx={{ height: "14rem" }}>
                <PanelHeader icon="trending_down" title="Motor Cortex ERD Trend" color="success" />
                <MDBox px={3} pt={1}>
                  <MDTypography variant="button" color="text" fontStyle="italic">
                    No session data yet.
                  </MDTypography>
                </MDBox>
              </Card>
            )}
          </Grid>
        </Grid>

        {/* ── Row 4: Progress summary stats ───────────────────────────── */}
        {progress && (
          <Card>
            <PanelHeader icon="insights" title="Recovery Summary" color="success" />
            <MDBox px={3} pb={3}>
              <Grid container spacing={3}>
                <Grid item xs={6} md={3}>
                  <StatBox label="Total Sessions"   value={progress.totalSessions} color="info" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatBox label="Asymmetry Change" value={progress.asymmetryChange?.toFixed(3)} color={progress.asymmetryChange < 0 ? "success" : "warning"} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatBox label="ERD Change"       value={`${progress.erdChange?.toFixed(1)}%`} color={progress.erdChange < -5 ? "success" : "text"} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatBox label="ERD Present Now"  value={progress.erdNowPresent ? "Yes" : "No"} color={progress.erdNowPresent ? "success" : "error"} />
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        )}

      </MDBox>
    </DashboardLayout>
  );
}

/* ── small helper ──────────────────────────────────────────────────────── */
function LabelValue({ label, value, color, bold }) {
  return (
    <MDBox display="flex" justifyContent="space-between" py={0.5}>
      <MDTypography variant="button" color="text">{label}</MDTypography>
      <MDTypography variant="button" color={color || "text"} fontWeight={bold ? "bold" : "regular"}>
        {value ?? "—"}
      </MDTypography>
    </MDBox>
  );
}
